import React, { useState } from 'react';
import { Star, LogOut } from 'lucide-react';
import { useUI, useAuth, useActions } from '../context/AppContext';
import { USER_ROLE, Z_INDEX } from '../utils/constants';
import LogoutButton from '../components/ui/LogoutButton';

const SurveyModal = () => {
  const { loggedInUser, handleLogout, setLoggedInUser } = useAuth();
  const { showToast } = useUI();
  const { updateStudentData, submitRating } = useActions();
  const [rating, setRating] = useState(0);
  const [hoveredRating, setHoveredRating] = useState(0);
  const [submitting, setSubmitting] = useState(false);

  const reviewData =
    loggedInUser?.role === USER_ROLE.STUDENT ? loggedInUser?.pendingReview : null;
  if (!reviewData) return null;

  const clearPendingReview = async () => {
    const ok = await updateStudentData(loggedInUser.id, { pendingReview: null });
    if (ok) {
      setLoggedInUser((prev) => (prev ? { ...prev, pendingReview: null } : prev));
    }
    return ok;
  };

  const handleSubmit = async () => {
    if (rating === 0) return showToast('Lütfen bir puan seçin.', 'error');
    if (submitting) return;
    setSubmitting(true);

    try {
      try {
        await submitRating(reviewData.instructorEmail, rating);
      } catch (err) {
        const alreadyRated = /zaten değerlendirdiniz/i.test(err.message || '');
        if (!alreadyRated) throw err;
      }
      const cleared = await clearPendingReview();
      if (cleared) {
        showToast('Geri bildiriminiz kaydedildi.');
      }
    } catch (err) {
      showToast(err.message || 'Değerlendirme kaydedilemedi. Daha sonra hatırlatabilirsiniz.', 'error');
    } finally {
      setSubmitting(false);
    }
  };

  const handleSkip = async () => {
    if (submitting) return;
    setSubmitting(true);
    try {
      const cleared = await clearPendingReview();
      if (cleared) {
        showToast('Değerlendirmeyi daha sonra ayarlardan yapabilirsiniz.');
      } else {
        showToast('İşlem tamamlanamadı, lütfen tekrar deneyin.', 'error');
      }
    } catch (err) {
      showToast(err.message || 'İşlem tamamlanamadı.', 'error');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 bg-slate-900/80 backdrop-blur-md flex items-center justify-center animate-in fade-in p-4 pt-[max(1rem,env(safe-area-inset-top))]"
      style={{ zIndex: Z_INDEX.survey }}
      role="dialog"
      aria-modal="true"
      aria-labelledby="survey-title"
    >
      <div className="bg-white dark:bg-slate-900 rounded-3xl shadow-2xl w-full max-w-md p-6 sm:p-8 text-center border border-slate-100 dark:border-slate-800">
        <div className="w-16 h-16 sm:w-20 sm:h-20 bg-amber-100 dark:bg-amber-900/30 rounded-full flex items-center justify-center text-amber-500 mx-auto mb-4 sm:mb-6">
          <Star size={32} className="fill-amber-500 sm:w-10 sm:h-10" />
        </div>
        <h3 id="survey-title" className="font-bold text-xl sm:text-2xl text-slate-800 dark:text-white mb-2">
          Eğitmen Değerlendirmesi
        </h3>
        <p className="text-slate-500 dark:text-slate-400 text-xs sm:text-sm mb-6 sm:mb-8 leading-relaxed">
          <strong>{reviewData.instructorName}</strong> ile olan çalışma süreciniz sona erdi.
          Lütfen aldığınız eğitimi puanlayın.
        </p>
        <div className="flex justify-center gap-2 sm:gap-3 mb-6 sm:mb-8" role="group" aria-label="Puan seçimi">
          {[1, 2, 3, 4, 5].map((star) => (
            <button
              key={star}
              type="button"
              aria-label={`${star} yıldız`}
              aria-pressed={rating === star}
              onMouseEnter={() => setHoveredRating(star)}
              onMouseLeave={() => setHoveredRating(0)}
              onClick={() => setRating(star)}
              className="transition-transform hover:scale-110 focus:outline-none focus-visible:ring-2 focus-visible:ring-amber-400 rounded"
            >
              <Star
                size={32}
                className={`transition-colors sm:w-9 sm:h-9 ${
                  star <= (hoveredRating || rating)
                    ? 'text-amber-500 fill-amber-500'
                    : 'text-slate-200 dark:text-slate-700 fill-transparent'
                }`}
              />
            </button>
          ))}
        </div>
        <div className="space-y-3">
          <button
            type="button"
            disabled={submitting}
            onClick={handleSubmit}
            className="w-full bg-slate-900 dark:bg-blue-600 text-white py-3 sm:py-4 rounded-xl font-bold hover:bg-slate-800 dark:hover:bg-blue-700 transition-colors shadow-md text-sm sm:text-base disabled:opacity-60"
          >
            {submitting ? 'Kaydediliyor...' : 'Gönder ve Devam Et'}
          </button>
          <button
            type="button"
            disabled={submitting}
            onClick={handleSkip}
            className="w-full bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300 py-3 rounded-xl font-bold hover:bg-slate-200 dark:hover:bg-slate-700 transition-colors text-sm disabled:opacity-60"
          >
            Daha Sonra Hatırlat
          </button>
          <LogoutButton
            icon={LogOut}
            iconSize={16}
            disabled={submitting}
            onLogout={handleLogout}
            className="w-full flex items-center justify-center gap-2 text-slate-500 dark:text-slate-400 py-2 text-sm font-medium hover:text-slate-700 dark:hover:text-slate-200 transition-colors disabled:opacity-60"
          >
            Çıkış Yap
          </LogoutButton>
        </div>
      </div>
    </div>
  );
};

export default SurveyModal;
